import { DEFAULT_STOCK_UNIT, convertStockQuantity, normalizeStockUnit } from "../local/stockUnits";
import { supabase } from "./client";
import { isSupabaseConfigured } from "./config";
import { withRequestDeadline } from "./requestDeadline";

const toSafeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeText = (value, maxLength = 120) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const canUseRemoteBranchStock = (branchId) =>
  Boolean(isSupabaseConfigured && supabase && normalizeText(branchId, 120));

const buildStockMapFromRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    const ingredientId = normalizeText(row?.ingredient_id, 120);

    if (!ingredientId) {
      return acc;
    }

    acc[ingredientId] = {
      unidad: normalizeStockUnit(row?.unit || DEFAULT_STOCK_UNIT),
      cantidad: String(toSafeNumber(row?.quantity, 0)),
      updatedAt: row?.updated_at || new Date().toISOString(),
    };

    return acc;
  }, {});

const buildMovementRows = ({
  branchId,
  applied = [],
  movementType = "order_consumption",
  referenceType = "manual_adjustment",
  referenceId = null,
  notes = "",
}) =>
  applied.map((entry) => ({
    branch_id: branchId,
    ingredient_id: entry.ingredientId,
    movement_type: normalizeText(movementType, 80) || "order_consumption",
    quantity:
      Number(entry.quantity || 0) *
      (movementType === "order_consumption" ? -1 : 1),
    unit: entry.unit || DEFAULT_STOCK_UNIT,
    reference_type: normalizeText(referenceType, 80) || null,
    reference_id: referenceId || null,
    notes: normalizeText(notes, 240) || null,
  }));

export const listBranchIngredientStock = async (branchId) => {
  const normalizedBranchId = normalizeText(branchId, 120);

  if (!canUseRemoteBranchStock(normalizedBranchId)) {
    return {};
  }

  const { data, error } = await withRequestDeadline((signal) => supabase
    .from("branch_ingredient_stock")
    .select("ingredient_id, quantity, unit, updated_at")
    .eq("branch_id", normalizedBranchId)
    .abortSignal(signal), {
      timeoutMs: 15000,
      message: "La consulta de stock tardo demasiado. Vuelve a intentar para ver los valores actuales.",
    });

  if (error) {
    throw new Error(
      normalizeText(
        error.message || "No pudimos leer el stock operativo de la sucursal.",
        240
      )
    );
  }

  return buildStockMapFromRows(data || []);
};

export const saveBranchIngredientStock = async (branchId, stockMap = {}) => {
  const normalizedBranchId = normalizeText(branchId, 120);

  if (!canUseRemoteBranchStock(normalizedBranchId)) {
    return { ok: false, skipped: true, reason: "remote_stock_disabled" };
  }

  const rows = Object.entries(stockMap || {}).map(([ingredientId, record]) => ({
    branch_id: normalizedBranchId,
    ingredient_id: normalizeText(ingredientId, 120),
    quantity: toSafeNumber(record?.cantidad ?? record?.quantity, 0),
    unit: normalizeStockUnit(record?.unidad || record?.unit || DEFAULT_STOCK_UNIT),
  }));

  if (rows.length === 0) {
    return { ok: true, stockMap: {} };
  }

  const { error } = await withRequestDeadline((signal) => supabase
    .from("branch_ingredient_stock")
    .upsert(rows, {
      onConflict: "branch_id,ingredient_id",
    }).abortSignal(signal), {
      timeoutMs: 15000,
      message: "No pudimos confirmar el guardado a tiempo. Comprueba el stock antes de volver a guardar.",
    });

  if (error) {
    throw new Error(
      normalizeText(
        error.message || "No pudimos guardar el stock operativo en Supabase.",
        240
      )
    );
  }

  return {
    ok: true,
    stockMap: rows.reduce((acc, row) => {
      acc[row.ingredient_id] = {
        unidad: row.unit,
        cantidad: String(row.quantity),
        updatedAt: new Date().toISOString(),
      };
      return acc;
    }, {}),
  };
};

export const consumeBranchIngredientStock = async (
  branchId,
  ingredientes = [],
  consumos = [],
  {
    referenceType = "operational_order",
    referenceId = null,
    notes = "",
  } = {}
) => {
  const normalizedBranchId = normalizeText(branchId, 120);

  if (!canUseRemoteBranchStock(normalizedBranchId)) {
    return { ok: false, skipped: true, reason: "remote_stock_disabled" };
  }

  const aggregatedByIngredient = new Map();

  (Array.isArray(consumos) ? consumos : []).forEach((consumo) => {
    const quantity = toSafeNumber(consumo?.cantidad ?? consumo?.quantity, 0);

    if (quantity <= 0) {
      return;
    }

    const ingredientId = normalizeText(
      consumo?.ingredientId || consumo?.id,
      120
    );

    if (!ingredientId) {
      return;
    }

    const previous = aggregatedByIngredient.get(ingredientId);

    aggregatedByIngredient.set(ingredientId, {
      ingredientId,
      ingredientName:
        normalizeText(consumo?.ingredientName || consumo?.nombre, 80) ||
        previous?.ingredientName ||
        normalizeText(
          ingredientes.find((ingredient) => ingredient?.id === ingredientId)?.nombre,
          80
        ) ||
        "Ingrediente",
      quantity: Number(previous?.quantity || 0) + quantity,
      unit:
        normalizeText(
          consumo?.unit || consumo?.unidad,
          12
        ) || previous?.unit || DEFAULT_STOCK_UNIT,
    });
  });

  const requested = Array.from(aggregatedByIngredient.values());

  if (requested.length === 0) {
    return { ok: true, applied: [], stockMap: await listBranchIngredientStock(normalizedBranchId) };
  }

  const ingredientIds = requested.map((entry) => entry.ingredientId);
  const { data: stockRows, error: stockError } = await supabase
    .from("branch_ingredient_stock")
    .select("ingredient_id, quantity, unit, updated_at")
    .eq("branch_id", normalizedBranchId)
    .in("ingredient_id", ingredientIds);

  if (stockError) {
    throw new Error(
      normalizeText(
        stockError.message || "No pudimos revisar el stock de la sucursal.",
        240
      )
    );
  }

  const stockByIngredientId = new Map(
    (stockRows || []).map((row) => [normalizeText(row.ingredient_id, 120), row])
  );
  const updates = [];
  const applied = [];

  requested.forEach((entry) => {
    const stockRow = stockByIngredientId.get(entry.ingredientId);

    if (!stockRow) {
      throw new Error(
        `Falta cargar stock para ${entry.ingredientName || "este ingrediente"} en la sucursal.`
      );
    }

    const stockUnit = normalizeStockUnit(stockRow.unit || DEFAULT_STOCK_UNIT);
    const consumptionUnit = normalizeStockUnit(entry.unit || stockUnit);
    const normalizedQuantity = convertStockQuantity(
      entry.quantity,
      consumptionUnit,
      stockUnit
    );

    if (normalizedQuantity === null) {
      throw new Error(
        `La unidad de ${entry.ingredientName || "este ingrediente"} no coincide con su stock cargado.`
      );
    }

    const currentValue = toSafeNumber(stockRow.quantity, 0);

    if (currentValue < normalizedQuantity) {
      throw new Error(
        `No hay stock suficiente de ${entry.ingredientName || "este ingrediente"} para descontar el pedido.`
      );
    }

    const nextQuantity = Math.max(0, currentValue - normalizedQuantity);

    updates.push({
      branch_id: normalizedBranchId,
      ingredient_id: entry.ingredientId,
      quantity: nextQuantity,
      unit: stockUnit,
    });
    applied.push({
      ingredientId: entry.ingredientId,
      ingredientName: entry.ingredientName,
      quantity: normalizedQuantity,
      requestedQuantity: entry.quantity,
      unit: stockUnit,
      requestedUnit: consumptionUnit,
    });
  });

  const { error: updateError } = await supabase
    .from("branch_ingredient_stock")
    .upsert(updates, {
      onConflict: "branch_id,ingredient_id",
    });

  if (updateError) {
    throw new Error(
      normalizeText(
        updateError.message || "No pudimos descontar el stock de la sucursal.",
        240
      )
    );
  }

  const movementRows = buildMovementRows({
    branchId: normalizedBranchId,
    applied,
    movementType: "order_consumption",
    referenceType,
    referenceId,
    notes,
  });

  if (movementRows.length > 0) {
    const { error: movementError } = await supabase
      .from("stock_movements")
      .insert(movementRows);

    if (movementError) {
      console.warn(
        "No pudimos registrar los movimientos de stock de esta operacion:",
        movementError
      );
    }
  }

  return {
    ok: true,
    applied,
    stockMap: updates.reduce((acc, row) => {
      acc[row.ingredient_id] = {
        unidad: row.unit,
        cantidad: String(row.quantity),
        updatedAt: new Date().toISOString(),
      };
      return acc;
    }, {}),
  };
};

export const restoreBranchIngredientStock = async (
  branchId,
  ingredientes = [],
  consumos = [],
  {
    referenceType = "operational_order_restore",
    referenceId = null,
    notes = "",
  } = {}
) => {
  const normalizedBranchId = normalizeText(branchId, 120);

  if (!canUseRemoteBranchStock(normalizedBranchId)) {
    return { ok: false, skipped: true, reason: "remote_stock_disabled" };
  }

  const aggregatedByIngredient = new Map();

  (Array.isArray(consumos) ? consumos : []).forEach((consumo) => {
    const quantity = toSafeNumber(consumo?.cantidad ?? consumo?.quantity, 0);

    if (quantity <= 0) {
      return;
    }

    const ingredientId = normalizeText(
      consumo?.ingredientId || consumo?.id,
      120
    );

    if (!ingredientId) {
      return;
    }

    const previous = aggregatedByIngredient.get(ingredientId);

    aggregatedByIngredient.set(ingredientId, {
      ingredientId,
      ingredientName:
        normalizeText(consumo?.ingredientName || consumo?.nombre, 80) ||
        previous?.ingredientName ||
        normalizeText(
          ingredientes.find((ingredient) => ingredient?.id === ingredientId)?.nombre,
          80
        ) ||
        "Ingrediente",
      quantity: Number(previous?.quantity || 0) + quantity,
      unit:
        normalizeText(consumo?.unit || consumo?.unidad, 12) ||
        previous?.unit ||
        DEFAULT_STOCK_UNIT,
    });
  });

  const requested = Array.from(aggregatedByIngredient.values());

  if (requested.length === 0) {
    return {
      ok: true,
      applied: [],
      stockMap: await listBranchIngredientStock(normalizedBranchId),
    };
  }

  const ingredientIds = requested.map((entry) => entry.ingredientId);
  const { data: stockRows, error: stockError } = await supabase
    .from("branch_ingredient_stock")
    .select("ingredient_id, quantity, unit, updated_at")
    .eq("branch_id", normalizedBranchId)
    .in("ingredient_id", ingredientIds);

  if (stockError) {
    throw new Error(
      normalizeText(
        stockError.message || "No pudimos revisar el stock de la sucursal.",
        240
      )
    );
  }

  const stockByIngredientId = new Map(
    (stockRows || []).map((row) => [normalizeText(row.ingredient_id, 120), row])
  );
  const updates = [];
  const applied = [];

  requested.forEach((entry) => {
    const stockRow = stockByIngredientId.get(entry.ingredientId);
    const stockUnit = normalizeStockUnit(stockRow?.unit || DEFAULT_STOCK_UNIT);
    const consumptionUnit = normalizeStockUnit(entry.unit || stockUnit);
    const normalizedQuantity = convertStockQuantity(
      entry.quantity,
      consumptionUnit,
      stockUnit
    );

    if (normalizedQuantity === null) {
      throw new Error(
        `La unidad de ${entry.ingredientName || "este ingrediente"} no coincide con su stock cargado.`
      );
    }

    const currentValue = toSafeNumber(stockRow?.quantity, 0);
    const nextQuantity = Math.max(0, currentValue + normalizedQuantity);

    updates.push({
      branch_id: normalizedBranchId,
      ingredient_id: entry.ingredientId,
      quantity: nextQuantity,
      unit: stockUnit,
    });
    applied.push({
      ingredientId: entry.ingredientId,
      ingredientName: entry.ingredientName,
      quantity: normalizedQuantity,
      requestedQuantity: entry.quantity,
      unit: stockUnit,
      requestedUnit: consumptionUnit,
    });
  });

  const { error: updateError } = await supabase
    .from("branch_ingredient_stock")
    .upsert(updates, {
      onConflict: "branch_id,ingredient_id",
    });

  if (updateError) {
    throw new Error(
      normalizeText(
        updateError.message || "No pudimos reponer el stock de la sucursal.",
        240
      )
    );
  }

  const movementRows = buildMovementRows({
    branchId: normalizedBranchId,
    applied,
    movementType: "order_restore",
    referenceType,
    referenceId,
    notes,
  });

  if (movementRows.length > 0) {
    const { error: movementError } = await supabase
      .from("stock_movements")
      .insert(movementRows);

    if (movementError) {
      console.warn(
        "No pudimos registrar los movimientos de reposicion de stock:",
        movementError
      );
    }
  }

  return {
    ok: true,
    applied,
    stockMap: updates.reduce((acc, row) => {
      acc[row.ingredient_id] = {
        unidad: row.unit,
        cantidad: String(row.quantity),
        updatedAt: new Date().toISOString(),
      };
      return acc;
    }, {}),
  };
};
